import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronRight, Building, Building2, Plus, Edit2, Trash2, AlertCircle } from 'lucide-react'
import { apiJson } from '../api'
import { canDeleteOrgUnits, canManageOrgUnits } from '../auth'

interface OrgUnit {
  id: string
  name: string
  code: string
  description?: string
  parentId: string | null
  children?: OrgUnit[]
}

function countDescendants(unit: OrgUnit): number {
  if (!unit.children) return 0
  return unit.children.reduce((sum, c) => sum + 1 + countDescendants(c), 0)
}

interface NodeProps {
  unit: OrgUnit
  level?: number
  allowManage: boolean
  allowDelete: boolean
  busyId: string | null
  onEdit: (unit: OrgUnit) => void
  onDelete: (unit: OrgUnit) => void
}

function OrgUnitNode({ unit, level = 0, allowManage, allowDelete, busyId, onEdit, onDelete }: NodeProps) {
  const [expanded, setExpanded] = useState(level < 2)
  const hasChildren = !!unit.children && unit.children.length > 0
  const descendants = countDescendants(unit)

  return (
    <div className="relative">
      {/* Connector line for nested levels */}
      {level > 0 && (
        <span
          className="absolute top-0 h-full border-l border-slate-200"
          style={{ left: `${(level - 1) * 28 + 26}px` }}
        />
      )}
      <div
        className={`group relative flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors ${
          hasChildren ? 'cursor-pointer hover:bg-slate-50' : 'cursor-default'
        }`}
        style={{ marginLeft: `${level * 28}px`, width: `calc(100% - ${level * 28}px)` }}
        onClick={() => hasChildren && setExpanded(!expanded)}
      >
        <span
          className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md transition-transform ${
            hasChildren ? 'text-slate-400' : 'text-transparent'
          }`}
        >
          <ChevronRight
            size={15}
            className={`transition-transform duration-200 ${expanded ? 'rotate-90' : ''}`}
          />
        </span>
        <span
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
            level === 0
              ? 'bg-gradient-to-br from-brand-500 to-brand-700 text-white shadow-sm'
              : 'bg-brand-50 text-brand-500'
          }`}
        >
          {level === 0 ? <Building size={16} /> : <Building2 size={16} />}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-semibold">{unit.name}</span>
          <span className="block text-xs text-slate-400">
            {unit.code}
            {hasChildren && ` · ${descendants} sub-unit${descendants === 1 ? '' : 's'}`}
          </span>
        </span>
        {hasChildren && (
          <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-500">
            {unit.children!.length}
          </span>
        )}
        {(allowManage || (allowDelete && !hasChildren)) && (
          <span
            className="inline-flex shrink-0 gap-1 opacity-0 transition-opacity group-hover:opacity-100"
            onClick={(e) => e.stopPropagation()}
          >
            {allowManage && (
              <button
                onClick={() => onEdit(unit)}
                className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-brand-50 hover:text-brand-600"
                title="Rename / edit"
              >
                <Edit2 size={14} />
              </button>
            )}
            {allowDelete && !hasChildren && (
              <button
                onClick={() => onDelete(unit)}
                disabled={busyId === unit.id}
                className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600 disabled:opacity-40"
                title="Delete (admin only, leaf units)"
              >
                <Trash2 size={14} />
              </button>
            )}
          </span>
        )}
      </div>
      {expanded && hasChildren && (
        <div>
          {unit.children!.map((child) => (
            <OrgUnitNode
              key={child.id}
              unit={child}
              level={level + 1}
              allowManage={allowManage}
              allowDelete={allowDelete}
              busyId={busyId}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default function OrgUnits() {
  const navigate = useNavigate()
  const [orgUnits, setOrgUnits] = useState<OrgUnit[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [actionError, setActionError] = useState('')
  const [busyId, setBusyId] = useState<string | null>(null)

  const allowManage = canManageOrgUnits()
  const allowDelete = canDeleteOrgUnits()

  const fetchAll = useCallback(async () => {
    try {
      const tree = await apiJson<OrgUnit[]>('/org-units/tree')
      setOrgUnits(tree)
      setLoadError('')
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load org units')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchAll()
  }, [fetchAll])

  const totalUnits = useMemo(
    () => orgUnits.reduce((sum, u) => sum + 1 + countDescendants(u), 0),
    [orgUnits],
  )

  const remove = async (unit: OrgUnit) => {
    if (!window.confirm(`Delete org unit “${unit.name}” (${unit.code})?`)) return
    setBusyId(unit.id)
    setActionError('')
    try {
      await apiJson(`/org-units/${unit.id}`, { method: 'DELETE' })
      await fetchAll()
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Delete failed')
    } finally {
      setBusyId(null)
    }
  }

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-brand-600" />
      </div>
    )
  }

  return (
    <div className="space-y-5">
      {(loadError || actionError) && (
        <div className="flex items-center gap-2.5 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
          <AlertCircle size={16} className="shrink-0" />
          {loadError || actionError}
        </div>
      )}

      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="mb-4 flex items-center justify-between border-b border-slate-100 pb-4">
          <div>
            <h3 className="text-sm font-bold tracking-tight">Organization structure</h3>
            <p className="mt-0.5 text-xs text-slate-400">
              {totalUnits} unit{totalUnits === 1 ? '' : 's'} · click a unit to expand or collapse
              {!allowManage && ' · read-only access'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {allowManage && (
              <button
                onClick={() => navigate('/org-units/new')}
                className="flex items-center gap-2 rounded-xl bg-gradient-to-r from-brand-600 to-brand-700 px-3.5 py-2 text-xs font-semibold text-white shadow-lg shadow-brand-500/25 transition-all hover:brightness-110"
              >
                <Plus size={14} />
                Add Unit
              </button>
            )}
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-50 text-brand-500">
              <Building size={18} />
            </span>
          </div>
        </div>
        <div className="space-y-1">
          {orgUnits.map((unit) => (
            <OrgUnitNode
              key={unit.id}
              unit={unit}
              allowManage={allowManage}
              allowDelete={allowDelete}
              busyId={busyId}
              onEdit={(u) => navigate(`/org-units/${u.id}/edit`, { state: { unit: u } })}
              onDelete={remove}
            />
          ))}
          {orgUnits.length === 0 && !loadError && (
            <p className="py-10 text-center text-sm text-slate-400">No org units yet.</p>
          )}
        </div>
      </div>
    </div>
  )
}
